import { GroupedFormData } from '@navstation-portal/reporting/data-model/ui';
import {
  IReportTabState,
  InitMode,
  StateField,
  StateFieldType,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { FormConfig, VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { type } from '@ngrx/signals';
import { EventCreator, eventGroup } from '@ngrx/signals/events';

export interface FormStoreEventPayload {
  sectionId: string;
  fieldId: string;
  value: StateField<StateFieldType> | StateFieldType;
  index?: number;
  property?: string;
}

// One Calculated Value for the store to hold. A row section names the row with
// `index` and the field in the row with `property`, as FormStoreEventPayload
// does.
export interface CalculatedValueWrite {
  sectionId: string;
  fieldId: string;
  value: number | undefined;
  index?: number;
  property?: string;
}

// The path of a Calculated Value as text, for a message or a map key. For
// example 'machineryRunningHours.auxEngines[0].difference'.
export const formatCalculatedValuePath = ({
  sectionId,
  fieldId,
  index,
  property,
}: Omit<CalculatedValueWrite, 'value'>): string =>
  index == null ? `${sectionId}.${fieldId}` : `${sectionId}.${fieldId}[${index}].${property}`;

export interface CalculatedValuesPayload {
  writes: CalculatedValueWrite[];
}

export interface FormStoreInitPayload {
  initialData: IReportTabState;
  initMode: InitMode;
  formFieldRules?: Record<string, GroupedFormData>;
  formConfig?: FormConfig;
  vesselSpec?: VesselSpec;
}

export interface FormStoreEvents {
  [key: string]: FormStoreEventPayload | FormStoreInitPayload | CalculatedValuesPayload;
  initialiseFormState: FormStoreInitPayload;
  updateFormStateTouched: FormStoreEventPayload;
  updateFormStatePristine: FormStoreEventPayload;
  updateFormStateStatus: FormStoreEventPayload;
  updateFormStateValue: FormStoreEventPayload;
  calculatedValuesChanged: CalculatedValuesPayload;
}

interface EventTypeMap {
  initialiseFormState: FormStoreInitPayload;
  updateFormStateTouched: FormStoreEventPayload;
  updateFormStatePristine: FormStoreEventPayload;
  updateFormStateStatus: FormStoreEventPayload;
  updateFormStateValue: FormStoreEventPayload;
  calculatedValuesChanged: CalculatedValuesPayload;
}

export type FormStoreEventGroup = {
  [K in keyof EventTypeMap]: EventCreator<`[${string}] ${K}`, EventTypeMap[K]>;
};

export function createFormStoreEvents(storeName: string): FormStoreEventGroup {
  return eventGroup<string, FormStoreEvents>({
    source: storeName,
    events: {
      initialiseFormState: type<FormStoreInitPayload>(),
      updateFormStateTouched: type<FormStoreEventPayload>(),
      updateFormStatePristine: type<FormStoreEventPayload>(),
      updateFormStateStatus: type<FormStoreEventPayload>(),
      updateFormStateValue: type<FormStoreEventPayload>(),
      // The store writes these values itself, so no form control sends them.
      // See withCalculatedValues.
      calculatedValuesChanged: type<CalculatedValuesPayload>(),
    },
  });
}
